import { User } from 'core/interfaces/userInterface';
import { apiService } from 'core/services/apiService';
import { getTokenFromLocalStorage } from 'core/services/axiosInterceptors';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { Role } from 'module/schedule/interfaces/ScheduleInterface';
import { createContext, useContext, useEffect, useState } from 'react';
import { ApiErrorResponse } from 'types';
import useUserDataStore from 'store/useUserDataStore';
interface ContextProps {
	user: any;
	login: any;
	error: any;
	isLoggedin: any;
	isWorkplaceSelected: any;
	setUserFromLocaleStorage: any;
	logout: any;
	role: Role[] | null;
	setRolesToLocal: any;
}

interface FormValues {
	email: string;
	password: string;
	tenant: string;
}

const authContext = createContext({} as ContextProps);

function ProvideAuth({ children }: { children: React.ReactNode }): JSX.Element {
	const auth = useProvideAuth();
	return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

function useAuth(): ContextProps {
	return useContext(authContext);
}

function useProvideAuth() {
	const [user, setUser] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [role, setRole] = useState<Role[] | null>(null);

	// Access the user data store
	const {
		fetchSecurityRoles,
		fetchHierarchyNodes,
		setCurrentUserRole,
		clearUserData,
		initializeFromLocalStorage,
	} = useUserDataStore();

	const login = async (data: FormValues) => {
		try {
			setError(null);
			// Convert email to lowercase before sending to API (with null check)
			const loginData = { ...data, email: data.email ? data.email.toLowerCase() : '' };
			const { user, tokenKey, refreshTokenKey, expiresAt, usedBy, refreshExpiresAt } =
				apiService.responseHandler(await apiService.post('/token-web', loginData));
			// console.log('token details', user, tokenKey, refreshTokenKey);

			localStorage.setItem(
				'BYOD-app',
				JSON.stringify({
					token_key: tokenKey,
					refresh_token_key: refreshTokenKey,
					refreshExpiresAt,
					expiresAt,
					usedBy,
					role: [],
					workplace: null,
				})
			);

			const userDetails = apiService.responseHandler(await apiService.get(`/users/me`));
			// console.log('user details', userDetails);
			if (userDetails?.id) {
				localStorage.setItem('userId', userDetails.id.toString());
			}

			// If user has a security role in the /me response, store it
			if (userDetails?.securityRole) {
				setCurrentUserRole(userDetails.securityRole);
			}

			setUser(userDetails);

			localStorage.setItem(
				'BYOD-app',
				JSON.stringify({
					user: userDetails,
					token_key: tokenKey,
					refresh_token_key: refreshTokenKey,
					refreshExpiresAt,
					expiresAt,
					usedBy,
					role: [],
					workplace: null,
				})
			);

			// Fetch security roles and hierarchy nodes after successful login
			try {
				// console.log('Fetching security roles and hierarchy nodes...');
				await Promise.all([fetchSecurityRoles(), fetchHierarchyNodes()]);
				// console.log('Security roles and hierarchy nodes fetched successfully');
			} catch (apiError) {
				console.error('Failed to fetch security roles or hierarchy nodes:', apiError);
				// Don't fail the login if these API calls fail
			}

			return true;
		} catch (error: any) {
			console.log('login error', error, typeof error);

			// Normalize error text from different shapes (string, Error-like, API response)
			const errorText: string =
				typeof error === 'string'
					? error
					: error && typeof error === 'object'
					? (error.message || error.error || JSON.stringify(error))
					: '';

			// If the server returned the specific access denied message, show the friendly message
			if (/access denied/i.test(errorText) || /invalid credentials/i.test(errorText) || /user deactivated/i.test(errorText)) {
				setError('Log In Failed, Please try again or reset your Password.');
			} else if (typeof error === 'string') {
				// pass through arbitrary string errors
				setError(error);
			} else if (error && typeof error === 'object' && typeof error.message === 'string') {
				setError(error.message);
			} else {
				setError('Log In Failed, Please try again or reset your Password.');
			}

			return false;
		}
	};

	const isLoggedin = () => {
		if (getTokenFromLocalStorage('token_key')) {
			return true;
		}
		return false;
	};

	const isWorkplaceSelected = () => {
		if (getTokenFromLocalStorage('workplace')) {
			return true;
		}
		return false;
	};

	const setUserFromLocaleStorage = () => {
		const user = getTokenFromLocalStorage('user');
		const role = getTokenFromLocalStorage('role');
		setUser(user);
		setRole(role);
	};

	const logout = async () => {
		// Clear user data from store
		clearUserData();

		// Save values we want to preserve
		const savedOrgId = localStorage.getItem('BYOD-organizationId');
		
		// Clear GameMaster-related cache
		try {
			const { clearGameMasterCache } = await import('core/utils/localStorageUtils');
			// Clear all GameMaster cache (no workplaceId means clear all)
			clearGameMasterCache();
		} catch (e) {
			console.warn('Failed to clear GameMaster cache on logout:', e);
		}

		// Clear shift request types cache
		try {
			const { clearShiftRequestTypesCache } = await import('core/utils/shiftRequestTypesCache');
			clearShiftRequestTypesCache();
		} catch (e) {
			console.warn('Failed to clear shift request types cache on logout:', e);
		}

		// Clear essential auth-related items
		localStorage.removeItem('BYOD-app');
		localStorage.removeItem('userId');
		localStorage.removeItem('workplace-id');
		localStorage.removeItem('workplace-postcode');
		localStorage.removeItem('workplace_timezone');
		localStorage.removeItem('BYOD-user-role');
		
		// Restore organizationId if it existed
		if (savedOrgId) {
			localStorage.setItem('BYOD-organizationId', savedOrgId);
		}
		
		window.location.href = ROUTES.LOGIN;
	};



	const setRolesToLocal = async (roles: Role[]) => {
		setTimeout(async () => {
			const data = await JSON.parse(localStorage.getItem('BYOD-app')!);
			data.role = roles;
			localStorage.setItem('BYOD-app', JSON.stringify(data));
			setRole(roles);
		}, 200); // after workplace finish
	};

	useEffect(() => {
		setUserFromLocaleStorage();
		// Initialize user data from localStorage
		initializeFromLocalStorage();
	}, [initializeFromLocalStorage]);
	// console.log("auth context user", user);
	return {
		user,
		login,
		error,
		isLoggedin,
		isWorkplaceSelected,
		setUserFromLocaleStorage,
		logout,
		setRolesToLocal,
		role,
	};
}

export { ProvideAuth, useAuth };
